import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  HomeIcon,
  UsersIcon,
  UserIcon,
  ArrowLeftOnRectangleIcon,
  CurrencyDollarIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../../hooks/useAuth";
import { usePermissions, UserRole } from "../../hooks/usePermissions";
import heightenLogo from "../../assets/images/heighten-logo.png";

interface MenuItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  children?: MenuItem[];
  requiredRoles?: string[];
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { logout } = useAuth();
  const { hasAccess } = usePermissions();

  const allMenuItems: MenuItem[] = [
    {
      label: "Dashboard",
      icon: HomeIcon,
      href: "/",
    },
    {
      label: "Users",
      icon: UsersIcon,
      href: "/users",
      requiredRoles: ["ADMIN"],
    },
    {
      label: "Cash Outs",
      icon: CurrencyDollarIcon,
      href: "/cash-outs",
    },
    {
      label: "Email Testing",
      icon: EnvelopeIcon,
      href: "/admin/email-testing",
      requiredRoles: ["ADMIN"],
    },
    {
      label: "Profile",
      icon: UserIcon,
      href: "/profile",
    },
  ];

  // Filter menu items based on user permissions
  const menuItems = allMenuItems.filter((item) => {
    if (!item.requiredRoles || item.requiredRoles.length === 0) {
      return true;
    }
    const hasPermission = hasAccess(item.requiredRoles as UserRole[]);
    console.log(
      `Sidebar: Checking ${item.label}, required: ${item.requiredRoles}, hasAccess: ${hasPermission}`,
    );
    return hasPermission;
  });

  const isActive = (href: string) => {
    if (href === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(href);
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && <div className="menu-overlay lg:hidden" onClick={onClose} />}

      {/* Sidebar */}
      <div className={`side-menu ${isOpen ? "active" : ""}`}>
        {/* Logo */}
        <div className="flex items-center px-8 py-6">
          <Link to="/">
            <img
              src={heightenLogo}
              alt="Heighten Management"
              className="cursor-pointer"
            />
          </Link>
        </div>

        {/* Navigation */}
        <nav className="px-5 pb-5">
          <div className="mb-2">
            <div className="text-xs font-medium text-slate-400 uppercase tracking-wider px-3 mb-2">
              Main Menu
            </div>
            <ul className="space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);

                return (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      className={`
                        flex items-center px-3 py-2.5 rounded-lg transition-all duration-200
                        ${
                          active
                            ? "bg-slate-700/50 sidebar-link-active"
                            : "hover:bg-slate-700/30"
                        }
                      `}
                      onClick={() => isOpen && onClose()}
                    >
                      <Icon className="w-5 h-5 mr-3" />
                      <span className="font-medium text-inherit">
                        {item.label}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="mt-8">
            <div className="text-xs font-medium text-slate-400 uppercase tracking-wider px-3 mb-2">
              Account
            </div>
            <ul className="space-y-1">
              <li>
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full px-3 py-2.5 rounded-lg transition-all duration-200 hover:bg-slate-700/30"
                >
                  <ArrowLeftOnRectangleIcon className="w-5 h-5 mr-3" />
                  <span className="font-medium">Logout</span>
                </button>
              </li>
            </ul>
          </div>
        </nav>
      </div>
    </>
  );
};

export default Sidebar;
